// src/components7/Header.js
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../images/recipeailogo.jpg";
import { FaChevronLeft, FaSync, FaSignInAlt, FaCrown, FaUser } from "react-icons/fa";
import secureLocalStorage from "react-secure-storage";
import { forceLogoutAndPurge, getStoredProfilePic } from "../utils/sessionUtils";
import { useSubscription } from "../context/SubscriptionContext";
import config from "../config.json";

const Header = ({ title: customTitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isSubscribed } = useSubscription();

  const loggedInUserEmail =
    secureLocalStorage.getItem("loggedInUserEmail") ||
    (typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("loggedInUserEmail")) ||
    "";
  
  const rawUser = secureLocalStorage.getItem("loggedInUser");
  const parsedUser = rawUser ? (typeof rawUser === "string" ? JSON.parse(rawUser) : rawUser) : null;
  const rawGuest = secureLocalStorage.getItem("guestUser");
  const parsedGuest = rawGuest ? (typeof rawGuest === "string" ? JSON.parse(rawGuest) : rawGuest) : null;

  const activeEmail = parsedUser?.email || loggedInUserEmail;
  const isGuest = !activeEmail || activeEmail === "guest";
  const rawName = parsedUser?.name || parsedGuest?.name || (isGuest ? "Guest" : activeEmail.split("@")[0]);
  const userInitial = (rawName || "U").charAt(0).toUpperCase();

  const [profilePic, setProfilePic] = useState(() => getStoredProfilePic(activeEmail) || parsedUser?.profilePic || "");

  useEffect(() => {
    const handleProfileUpdate = (e) => {
      if (e?.detail?.profilePic !== undefined) {
        setProfilePic(e.detail.profilePic);
      }
    };
    window.addEventListener("user-profile-updated", handleProfileUpdate);
    return () => window.removeEventListener("user-profile-updated", handleProfileUpdate);
  }, []);

  const handleLoginClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    forceLogoutAndPurge();
    window.location.replace("/");
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.dispatchEvent(new CustomEvent("refresh-page"));
    window.dispatchEvent(new CustomEvent("refresh-activity"));
    setTimeout(() => {
      setIsRefreshing(false);
      window.location.reload();
    }, 500);
  };

  const isSubPage = pathname !== "/" && pathname !== "";

  const getPageTitle = () => {
    if (pathname === "/" || pathname === "") return config.apptitle || "Recipe AI";
    if (pathname === "/caloriecheck") return "Calorie Check";
    if (pathname === "/history" || pathname === "/calhistory") return "History";
    if (pathname === "/profile" || pathname === "/manage" || pathname === "/support") return "Accounts";
    if (pathname === "/upgrade") return "Upgrade to Pro";
    if (pathname === "/blog") return "Blog";
    return customTitle || "Recipe AI";
  };

  const handleHeaderBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <header className="mobile-app-header d-flex justify-content-between align-items-center px-3">
      <div className="d-flex align-items-center gap-2">
        {isSubPage ? (
          <button
            type="button"
            className="btn btn-dark-glass p-0 d-flex align-items-center justify-content-center rounded-circle text-success flex-shrink-0"
            style={{ width: "36px", height: "36px" }}
            onClick={handleHeaderBack}
            aria-label="Back"
          >
            <FaChevronLeft size={14} className="text-success" />
          </button>
        ) : (
          <Link to="/" className="d-flex align-items-center text-decoration-none">
            <img
              src={logo}
              alt="Recipe AI"
              width="36"
              height="36"
              className="rounded-circle flex-shrink-0 shadow-sm"
              style={{
                width: "36px",
                height: "36px",
                objectFit: "cover",
                border: "2px solid #22c55e",
              }}
            />
          </Link>
        )}
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0 fw-bold text-dark fs-5">{getPageTitle()}</h4>
          <Link to="/upgrade" className="text-decoration-none">
            <span
              className={`badge rounded-pill d-inline-flex align-items-center gap-1 ${
                isSubscribed ? "bg-warning text-dark" : "bg-light text-secondary border"
              }`}
              style={{ fontSize: "10.5px", padding: "3px 8px" }}
            >
              {isSubscribed ? <><FaCrown size={10} className="text-dark" /> Pro</> : "Upgrade"}
            </span>
          </Link>
        </div>
      </div>

      <div className="d-flex align-items-center gap-2">
        {process.env.REACT_APP_ENV === "QA" && (
          <span className="badge-pill-coral py-1 px-1.5" style={{ fontSize: "10px" }}>
            QA
          </span>
        )}
        <button
          type="button"
          className="btn btn-dark-glass p-0 d-flex align-items-center justify-content-center rounded-circle text-success flex-shrink-0"
          style={{ width: "36px", height: "36px" }}
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Refresh"
          aria-label="Refresh"
        >
          <FaSync size={13} className={`text-success ${isRefreshing ? "spin-icon" : ""}`} />
        </button>

        {/* User Icon on Top (Moved from Menu) - Click to open Profile */}
        <Link
          to="/profile"
          className={`header-avatar-btn ${
            pathname === "/profile" ||
            pathname === "/manage" ||
            pathname === "/support"
              ? "is-active"
              : ""
          }`}
          title={isGuest ? "Account Settings / Sign In" : `${rawName}'s Profile`}
          aria-label={isGuest ? "Account Settings" : "My Profile"}
        >
          {profilePic ? (
            <img
              src={profilePic}
              alt={rawName || "Profile"}
              className="header-avatar-img"
            />
          ) : !isGuest ? (
            <div className="header-avatar-initial">
              {userInitial}
            </div>
          ) : (
            <div className="header-avatar-guest">
              <FaUser size={14} />
            </div>
          )}
        </Link>
      </div>
    </header>
  );
};

export default Header;
